import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PublicLayout, PageContainer, PageHeader } from "@/components/PublicLayout";
import { useSeo } from "@/lib/useSeo";
import { EnvelopeSimple, Megaphone } from "@phosphor-icons/react";

export default function Contact() {
  useSeo({ title: "Contact Guwahati Morning Teer | Updates", description: "Contact Guwahati Morning Teer and read the latest updates and announcements.", path: "/contact" });
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const updatesQ = useQuery({ queryKey: ["updates"], queryFn: async () => (await api.get("/updates")).data });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Please enter your name and message.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent!");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <PageContainer className="py-10">
        <PageHeader eyebrow="Get In Touch" title="Contact / Updates" subtitle="Send us a message or read the latest announcements." />

        <a
          href="mailto:Shillongmorningteer4758@gmail.com"
          data-testid="contact-email-link"
          className="flex items-center gap-4 rounded-2xl bg-gradient-to-r from-brand-900 to-brand text-white p-5 mb-8 hover:-translate-y-0.5 transition-transform"
        >
          <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-white/15 flex-shrink-0">
            <EnvelopeSimple className="w-6 h-6" weight="duotone" />
          </span>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-violet-200">Email us directly</p>
            <p className="font-heading font-bold text-sm sm:text-lg break-all">Shillongmorningteer4758@gmail.com</p>
          </div>
        </a>

        <div className="grid gap-8 lg:grid-cols-2">
          <form onSubmit={submit} className="rounded-2xl bg-white border border-slate-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06)] p-6" data-testid="contact-form">
            <h3 className="font-heading font-bold text-lg text-slate-900 mb-4 flex items-center gap-2">
              <EnvelopeSimple className="w-5 h-5 text-brand" weight="duotone" /> Send a message
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Name</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="contact-name"
                  className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand/40"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email <span className="text-slate-400 font-normal">(optional)</span></label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="contact-email"
                  className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand/40"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Message</label>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="contact-message"
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand/40 resize-none"
                  placeholder="How can we help?"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="w-full h-12 rounded-xl bg-brand text-white font-bold hover:bg-brand-hover transition-colors disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Send Message"}
              </button>
            </div>
          </form>

          <div>
            <h3 className="font-heading font-bold text-lg text-slate-900 mb-4 flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-brand" weight="duotone" /> Latest Updates
            </h3>
            <div className="space-y-3" data-testid="updates-list">
              {(updatesQ.data || []).length === 0 ? (
                <p className="text-sm text-slate-500 bg-slate-50 rounded-xl p-4">No updates published yet.</p>
              ) : (
                updatesQ.data.map((u) => (
                  <div key={u.id} className="rounded-2xl bg-white border border-slate-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] p-4" data-testid={`update-item-${u.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-heading font-bold text-slate-900">{u.title}</p>
                      <span className="text-xs text-slate-400 font-semibold flex-shrink-0">{u.date}</span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1 leading-relaxed">{u.text}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </PageContainer>
    </PublicLayout>
  );
}
